import { Injector } from '@angular/core';
import { Routes } from '@angular/router';
import { UnderMaintenanceGuard, UnderMaintenancePage } from '@vsolv/core/downtime/web';
import { BUILT_IN_DASHBOARDS, BuiltInModel } from '@vsolv/packages/dashboarding/web';
import { PDFPageComponent } from '@vsolv/packages/pdf/web';
import {
  DISTRIBUTOR_PAGE_CONTENT,
  ExpiredDistributorLinkPage,
  OnboardDistributorPage,
  SettlementStatementPDFComponent,
} from '@wsphere/distributors/web';
import { InvoicePage } from '@wsphere/sales/web';
import {
  STAFF_PERMISSION_GLOBAL_ONLY_ROUTE_DATA,
  STAFF_PERMISSION_ROUTE_DATA,
  StaffRoleGuard,
} from '@wsphere/staff/web';
import { DeclarationPDFComponent, WarrantyPDFComponent } from '@wsphere/warranties/web';
import {
  AccountSetupGuard,
  IsStaffGuard,
  OnboardDistributorGuard,
  OneTimeTokenGuard,
  RefreshGuard,
  SignedInUserGuard,
  SignedOutGuard,
  TenantConfigGuard,
} from './guards';
import { LeftNavLayoutComponent } from './layouts';
import {
  AccountSetupPage,
  AssetsPage,
  ForgotPasswordPage,
  NotFoundPage,
  PasswordResetPage,
  RefreshPage,
  ReportingPage,
  SettingsPage,
  SignInPage,
} from './pages';
import { ComponentsPage } from './pages/components/components.page';
import { EDIItemPageResolver } from './pages/edi/item.page';
import {
  DistributorOnBoardingLinkResolver,
  EmailResolver,
  OneTimeTokenResolver,
  SignedInUserResolver,
  StaffResolver,
  TenantConfigResolver,
} from './resolvers';

export const routes: Routes = [
  { path: '404', component: NotFoundPage },
  { path: '503', component: UnderMaintenancePage },
  {
    path: '',
    canActivate: [UnderMaintenanceGuard, TenantConfigGuard],
    resolve: { config: TenantConfigResolver },
    children: [
      {
        path: 'pdf',
        component: PDFPageComponent,
        children: [
          {
            path: 'declaration',
            component: DeclarationPDFComponent,
          },
          {
            path: 'warranty',
            component: WarrantyPDFComponent,
          },
          {
            path: 'settlement-statement',
            component: SettlementStatementPDFComponent,
          },
        ],
      },
      { path: 'invoice', component: InvoicePage },
      {
        path: 'distributor-invoice-package',
        loadComponent: () =>
          import('./pages/distributor-invoice-package/distributor-invoice-package.page').then(
            mod => mod.DistributorInvoicePackagePage
          ),
      },
      {
        path: 'expired-link',
        component: ExpiredDistributorLinkPage,
      },
      {
        path: 'on-boarding/:linkId',
        canActivate: [OnboardDistributorGuard],
        resolve: { link: DistributorOnBoardingLinkResolver },
        component: OnboardDistributorPage,
      },
      {
        path: 'sign-in',
        component: SignInPage,
        canActivate: [SignedOutGuard],
      },
      {
        path: 'forgot-password',
        component: ForgotPasswordPage,
        canActivate: [SignedOutGuard],
      },
      {
        path: 'password-reset',
        component: PasswordResetPage,
        canActivate: [SignedOutGuard, OneTimeTokenGuard],
        resolve: { oneTimeToken: OneTimeTokenResolver, email: EmailResolver },
      },
      {
        path: '',
        canActivate: [SignedInUserGuard],
        resolve: { currentUser: SignedInUserResolver },
        children: [
          {
            path: 'account-setup',
            component: AccountSetupPage,
            canActivate: [AccountSetupGuard],
            resolve: { staff: StaffResolver },
          },
          {
            path: '',
            component: LeftNavLayoutComponent,
            canActivate: [IsStaffGuard],
            resolve: { staff: StaffResolver },
            children: [
              {
                path: 'refresh',
                component: RefreshPage,
                canActivate: [RefreshGuard],
              },
              {
                path: 'reporting/monthly-financials',
                loadComponent: () =>
                  import('./pages/reporting/monthly-financials').then(mod => mod.MonthlyFinancialsPage),
              },
              {
                path: 'reporting/bfs-financing',
                loadComponent: () => import('./pages/reporting/bfs-financing').then(mod => mod.BfsFinancingPage),
                data: { [STAFF_PERMISSION_ROUTE_DATA]: 'bfs_FinanceAdmin' },
                canActivate: [StaffRoleGuard],
              },
              {
                path: 'reporting',
                component: ReportingPage,
              },
              {
                path: 'sales',
                loadChildren: () => import('@wsphere/sales/web').then(mod => mod.SalesStaffPagesRoutingModule),
              },
              {
                path: 'policies',
                loadChildren: () => import('@wsphere/warranties/web').then(mod => mod.PolicyRoutingModule),
              },
              {
                path: 'distributors',
                loadChildren: () => import('@wsphere/distributors/web').then(mod => mod.DistributorRoutingModule),
                providers: [
                  {
                    provide: DISTRIBUTOR_PAGE_CONTENT,
                    useFactory: () =>
                      import('./components/distributor-page-sales.component').then(
                        comp => comp.DistributorPageSalesComponent
                      ),
                  },
                ],
              },
              {
                path: 'components',
                component: ComponentsPage,
              },
              {
                path: 'api-docs',
                loadComponent: () => import('./pages/api-docs/api-docs.page').then(m => m.ApiDocsPage),
                canActivate: [StaffRoleGuard],
                data: {
                  [STAFF_PERMISSION_ROUTE_DATA]: '*',
                  [STAFF_PERMISSION_GLOBAL_ONLY_ROUTE_DATA]: true,
                },
              },
              {
                path: 'api-docs/:surface',
                loadComponent: () => import('./pages/api-docs/api-docs-viewer.page').then(m => m.ApiDocsViewerPage),
                canActivate: [StaffRoleGuard],
                data: {
                  [STAFF_PERMISSION_ROUTE_DATA]: '*',
                  [STAFF_PERMISSION_GLOBAL_ONLY_ROUTE_DATA]: true,
                },
              },
              {
                path: 'user-settings',
                loadChildren: () => import('@wsphere/staff/web').then(m => m.StaffWebRoutingModule),
              },
              {
                path: 'claims',
                loadChildren: () => import('@wsphere/warranties/web').then(m => m.ClaimRoutingModule),
              },
              {
                path: 'provisioning',
                loadChildren: () => import('@wsphere/warranties/web').then(m => m.ProvisioningRoutingModule),
              },
              {
                path: 'warranty',
                loadChildren: () => import('@wsphere/warranties/web').then(m => m.WarrantyManagementRoutingModule),
              },
              {
                path: 'customers',
                loadChildren: () =>
                  import('@wsphere/customer-management/web').then(m => m.CustomerManagementRoutingModule),
              },
              {
                path: 'service-providers',
                loadChildren: () => import('@wsphere/service-providers/web').then(m => m.ServiceProvidersRoutingModule),
              },

              {
                path: 'settings',
                component: SettingsPage,
                data: { [STAFF_PERMISSION_ROUTE_DATA]: 'stf_ViewSettings' },
                canActivate: [StaffRoleGuard],
                children: [
                  {
                    path: '',
                    loadChildren: () => import('@wsphere/tenants/web').then(mod => mod.TenantWebRoutingModule),
                  },
                  {
                    path: 'assets',
                    component: AssetsPage,
                    children: [
                      {
                        path: '',
                        redirectTo: 'asset-types',
                        pathMatch: 'full',
                      },
                      {
                        path: 'properties',
                        loadComponent: () =>
                          import('./pages/settings/properties/properties.page').then(mod => mod.PropertiesPage),
                        data: { [STAFF_PERMISSION_ROUTE_DATA]: 'prp_ManageProperty' },
                        canActivate: [StaffRoleGuard],
                      },
                      {
                        path: 'asset-types',
                        loadComponent: () =>
                          import('@vsolv/packages/properties/web').then(m => m.AssetTypesListPageComponent),
                      },
                    ],
                  },
                  {
                    path: 'asset-types',
                    loadComponent: () =>
                      import('@vsolv/packages/properties/web').then(m => m.AssetTypesListPageComponent),
                  },
                  {
                    path: 'edi',
                    loadComponent: () => import('./pages/edi/edi.page').then(mod => mod.EDIPage),
                  },
                  {
                    path: 'edi/:source_file',
                    loadComponent: () => import('./pages/edi/file.page').then(mod => mod.EDIFilePage),
                  },
                  {
                    path: 'edi/:source_file/:id',
                    loadComponent: () => import('./pages/edi/item.page').then(mod => mod.EDIItemPage),
                    resolve: {
                      item: EDIItemPageResolver,
                    },
                  },
                ],
              },
              {
                path: 'distributor-settings',
                loadChildren: () =>
                  import('@wsphere/distributors/web').then(mod => mod.DistributorSettingsRoutingModule),
              },
              {
                path: 'documents',
                loadChildren: () => import('@vsolv/packages/storage/web').then(mod => mod.STORAGE_ROUTES),
              },
              {
                path: '',
                loadChildren: () => import('@vsolv/packages/dashboarding/web').then(mod => mod.DashboardRoutingModule),
                providers: [
                  {
                    multi: true,
                    provide: BUILT_IN_DASHBOARDS,
                    useValue: <BuiltInModel>{
                      id: 'dsh_builtin_warranties',
                      title: 'Warranties',
                      icon: 'bar-chart-square-01',
                      description: 'High-level program metrics',
                      component: (_injector: Injector) =>
                        import('./dashboards/warranties/warranties.component').then(mod => ({
                          type: mod.WarrantiesDashboardComponent,
                          inputs: {},
                        })),
                    },
                  },
                  {
                    multi: true,
                    provide: BUILT_IN_DASHBOARDS,
                    useValue: <BuiltInModel>{
                      id: 'dsh_builtin_claims',
                      title: 'Claims',
                      icon: 'bar-chart-square-01',
                      description: 'High-level program metrics',
                      component: (_injector: Injector) =>
                        import('./dashboards/claims/claims.component').then(mod => ({
                          type: mod.ClaimsDashboardComponent,
                          inputs: {},
                        })),
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
